import { Component, Input, OnInit } from '@angular/core';
import {
  ConsultaPaginada,
  ConsultaPaginadaResultado,
  Ordenacao,
  Restricao,
  RestricaoOperador,
  MensagemService,
  StringUtils,
} from 'maestro-ng';
import { LazyLoadEvent } from 'primeng/api/lazyloadevent';
import { SelectItem } from 'primeng/api/selectitem';
import { FonteDados } from '../fonte-dados.model';
import { FonteDadosService } from '../fonte-dados.service';
import { SituacaoProcessamento } from '../item-processamento.model';
import { PessoaProcessamentoMetadados } from './../pessoa-processamento-metadados.model';
import { FonteDadosMensagemErroService } from '../fonte-dados-mensagem-erro.service';

@Component({
  selector: 'app-fonte-dados-visualizador-pessoa-processamento',
  templateUrl: './fonte-dados-visualizador-pessoa-processamento.component.html',
  styleUrls: ['./fonte-dados-visualizador-pessoa-processamento.component.css'],
})
export class FonteDadosVisualizadorPessoaProcessamentoComponent implements OnInit {
  visivel = false;

  @Input()
  fonteDados: FonteDados;
  situacaoProcessamentoItens: SelectItem[];
  ds: ConsultaPaginadaResultado<PessoaProcessamentoMetadados> = { entidades: [], total: 0 };
  atributo: string;
  valor: any;
  id: string;
  erro: string;
  carregando = false;

  constructor(
    private service: FonteDadosService,
    private mensagemService: MensagemService,
    private fonteDadosMensagemErroService: FonteDadosMensagemErroService
  ) {}

  ngOnInit(): void {}

  mostrar(atributo: string, valor: any): void {
    this.visivel = true;
    this.atributo = atributo;
    this.valor = valor;
    this.id = undefined;
    this.erro = undefined;
    this.carregar();
  }

  carregar(): void {
    this.onLazyLoad(undefined);
  }

  onLazyLoad(event: LazyLoadEvent): void {
    if (this.visivel) {
      if (event === undefined) {
        event = { first: 0, rows: 30 };
      }

      this.carregando = true;

      const consulta = new ConsultaPaginada();
      consulta.contabilizar = true;
      consulta.inicio = event.first;
      consulta.limite = event.rows;
      consulta.ordenacoes = [];
      consulta.restricoes = [];

      if (event.sortField !== undefined) {
        consulta.ordenacoes.push(event.sortOrder ? Ordenacao.asc(event.sortField) : Ordenacao.desc(event.sortField));
      } else {
        const ordenacaoAtributo = this.atributo !== 'sincronizada' ? this.atributo + 'DataPendente' : 'sincronizacaoData';
        consulta.ordenacoes.push(Ordenacao.asc(ordenacaoAtributo));
      }

      if (this.valor !== undefined) {
        if (this.atributo.startsWith('ultimaAlteracao')) {
          consulta.restricoes.push(
            new Restricao<string>({ atributo: 'sincronizada', operador: RestricaoOperador.EQUALS, valor: true })
          );
        }
        const filtroAtributo = this.atributo !== 'sincronizada' ? this.atributo + 'Situacao' : this.atributo;
        consulta.restricoes.push(
          new Restricao<string>({ atributo: filtroAtributo, operador: RestricaoOperador.EQUALS, valor: this.valor })
        );
      }

      if (!StringUtils.isUndefinedOrEmpty(this.id)) {
        consulta.restricoes.push(new Restricao<string>({ atributo: 'id', operador: RestricaoOperador.EQUALS, valor: this.id }));
      }

      if (!StringUtils.isUndefinedOrEmpty(this.erro)) {
        consulta.restricoes.push(
          new Restricao<string>({ atributo: this.atributo + 'Erro', operador: RestricaoOperador.CONTAINS, valor: this.erro })
        );
      }

      this.service
        .recuperarPessoasProcessamentosMetadados(this.fonteDados, consulta)
        .then((resultado) => {
          this.ds = resultado;
          this.carregando = false;
        })
        .catch((resposta) => {
          this.carregando = false;

          this.fonteDadosMensagemErroService.apresentarErro(
            'Erro ao carregar as pessoas processamentos',
            'Pessoas processamentos',
            resposta
          );
        });
    }
  }

  onEnterSearch(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.carregar();
    }
  }

  limparFiltros(): void {
    this.erro = undefined;
    this.carregar();
  }

  isHabilitadoFiltroErro(): boolean {
    return this.atributo === 'sincronizada';
  }

  getSituacaoClasse(situacao: SituacaoProcessamento): string {
    switch (situacao) {
      case SituacaoProcessamento.PENDENTE:
        return 'alert-warning';
      case SituacaoProcessamento.PROCESSADO:
        return 'alert-success';
      case SituacaoProcessamento.FALHA_PROCESSAMENTO:
        return 'alert-danger';
      case SituacaoProcessamento.QUARENTENA:
        return 'alert-danger-orange';
      case SituacaoProcessamento.SUSPENSO:
        return 'alert-violet';
      case SituacaoProcessamento.INAPTO:
        return 'alert-info label-ignorado';
      case SituacaoProcessamento.EXCLUIDO:
        return 'alert-dark';
      default:
        return 'label-default';
    }
  }

  getSituacaoDescricao(situacao: SituacaoProcessamento): string {
    switch (situacao) {
      case SituacaoProcessamento.PENDENTE:
        return 'Pendente';
      case SituacaoProcessamento.PROCESSADO:
        return 'Processado';
      case SituacaoProcessamento.FALHA_PROCESSAMENTO:
        return 'Falha no processamento';
      case SituacaoProcessamento.QUARENTENA:
        return 'Quarentena';
      case SituacaoProcessamento.SUSPENSO:
        return 'Suspenso';
      case SituacaoProcessamento.INAPTO:
        return 'Inapto';
      case SituacaoProcessamento.EXCLUIDO:
        return 'Excluído';

      default:
        return '';
    }
  }

  possuiFalhas(entidade: PessoaProcessamentoMetadados) {
    return (
      entidade &&
      (entidade.comunicacao.numeroFalhas > 0 ||
        entidade.ultimaAlteracaoDocumentos.numeroFalhas > 0 ||
        entidade.ultimaAlteracaoEnderecos.numeroFalhas > 0 ||
        entidade.ultimaAlteracaoMetadados.numeroFalhas > 0 ||
        entidade.ultimaAlteracaoOutrosNomes.numeroFalhas > 0)
    );
  }

  isApresentarNumeroFalha(situacao: SituacaoProcessamento): boolean {
    return (
      situacao === SituacaoProcessamento.FALHA_PROCESSAMENTO ||
      situacao === SituacaoProcessamento.QUARENTENA ||
      situacao === SituacaoProcessamento.SUSPENSO
    );
  }
  getRotuloDataProcessamento(situacao: string): string {
    if (situacao === 'PROCESSADO') {
      return 'Processado em: ';
    } else if (situacao === 'EXCLUIDO') {
      return 'Excluído em: ';
    } else {
      return 'Últ. tentativa em: ';
    }
  }
}
